
import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import ProtectedRoute from '@/components/ProtectedRoute'
import { MetricCard } from '@/components/MetricCard'
import { EmptyMetricCard } from '@/components/EmptyMetricCard'
import { EmptyState } from '@/components/EmptyState'
import { QuickActions } from '@/components/QuickActions'
import { UpcomingEvents } from '@/components/UpcomingEvents'
import { RecentActivity } from '@/components/RecentActivity'
import { DepartmentChart } from '@/components/DepartmentChart'
import { EnhancedHomeScreen } from '@/components/EnhancedHomeScreen'
import { getRealEmployeeVacationData, calculateDashboardVacationAlerts } from '@/lib/dashboard-utils'
import {
  COMPANY_METRICS,
  formatCurrency as realFormatCurrency
} from '@/lib/real-data-service'
import { TechDirectorDashboardNew } from '@/components/dashboard/TechDirectorDashboardNew'
import {
  Users,
  TrendingUp,
  Clock,
  Calendar,
  Award,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Calculator,
  ChevronDown,
  BookOpen
} from 'lucide-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { getEmployeeDashboardMetrics, type EmployeeDashboardMetrics } from '@/lib/services/dashboard-metrics.service'
import { useNavigate } from '@tanstack/react-router'

function DashboardContent() {
  const { user } = useAuth()
  usePageTitle('Dashboard')
  const navigate = useNavigate()
  const [vacationAlerts, setVacationAlerts] = useState<any[]>([])
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(true)
  const [showDebug, setShowDebug] = useState(false)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [copied, setCopied] = useState(false)
  const [employeeMetrics, setEmployeeMetrics] = useState<EmployeeDashboardMetrics | null>(null)
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true)

  // User is guaranteed to exist here due to ProtectedRoute
  const isAdmin = user!.role === 'admin'
  const isManager = user!.role === 'diretor-executivo' || user!.role === 'diretor-financeiro' || user!.role === 'ceo' || user!.role === 'vice-presidente'

  // Gather diagnostic information for 'padrao' users
  useEffect(() => {
    if (user?.role === 'padrao') {
      const gatherDebugInfo = async () => {
        try {
          const session = await fetchAuthSession()
          const cognitoSub = session.tokens?.idToken?.payload?.sub
          const tokenExpiry = session.tokens?.accessToken?.payload?.exp
          const now = Math.floor(Date.now() / 1000)

          // Read actual API error from auth context (stored during getUserProfile)
          let apiError: string | null = null
          let clockSkewDetected = false
          let clockSkewMinutes = 0

          const storedError = localStorage.getItem('auth-api-error')
          if (storedError) {
            try {
              const errorInfo = JSON.parse(storedError)
              apiError = errorInfo.message
              clockSkewDetected = errorInfo.isClockSkew || false

              // Try to extract time difference from error message
              // Format: "20260107T210755Z is still later than 20260107T210452Z"
              if (clockSkewDetected && apiError && typeof apiError === 'string') {
                const match = apiError.match(/(\d{8}T\d{6}Z).*?(\d{8}T\d{6}Z)/)
                if (match) {
                  const clientTime = new Date(match[1].replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'))
                  const serverTime = new Date(match[2].replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'))
                  clockSkewMinutes = Math.round((clientTime.getTime() - serverTime.getTime()) / 60000)
                }
              }

              console.error('[DASHBOARD-PENDING-ACCESS] Using stored API error from auth context:', errorInfo)
            } catch (e) {
              console.error('[DASHBOARD-PENDING-ACCESS] Failed to parse stored error:', e)
            }
          }

          const info = {
            timestamp: new Date().toISOString(),
            userProfile: user,
            cognitoSub: cognitoSub || 'No Cognito sub found',
            tokenExpiry: tokenExpiry ? new Date(tokenExpiry * 1000).toISOString() : 'No token',
            tokenValid: tokenExpiry ? tokenExpiry > now : false,
            timeUntilExpiry: tokenExpiry ? `${Math.floor((tokenExpiry - now) / 60)} minutes` : 'N/A',
            sessionTokensPresent: !!session.tokens,
            identityId: session.identityId || 'No identity ID',
            apiError,
            clockSkewDetected,
            clockSkewMinutes: clockSkewDetected ? `${clockSkewMinutes > 0 ? '+' : ''}${clockSkewMinutes} minutes` : 'N/A'
          }

          setDebugInfo(info)
          console.error('[DASHBOARD-PENDING-ACCESS] Debug info:', info)
        } catch (error) {
          console.error('[DASHBOARD-PENDING-ACCESS] Error gathering debug info:', error)
          setDebugInfo({
            error: error instanceof Error ? error.message : 'Unknown error',
            userProfile: user
          })
        }
      }

      gatherDebugInfo()
    }
  }, [user])

  // Load vacation alerts asynchronously for regular employees
  useEffect(() => {
    const loadVacationAlerts = async () => {
      try {
        setIsLoadingAlerts(true)
        const realVacationData = await getRealEmployeeVacationData()
        const alerts = calculateDashboardVacationAlerts(realVacationData)
        setVacationAlerts(alerts)
      } catch (error) {
        console.error('Error loading vacation alerts:', error)
      } finally {
        setIsLoadingAlerts(false)
      }
    }

    // Only load for regular employees (managers get their data from EnhancedHomeScreen)
    if (!isManager && !isAdmin) {
      loadVacationAlerts()
    } else {
      setIsLoadingAlerts(false)
    }
  }, [isManager, isAdmin])

  // Load employee dashboard metrics for regular employees
  useEffect(() => {
    const loadEmployeeMetrics = async () => {
      try {
        setIsLoadingMetrics(true)
        const metrics = await getEmployeeDashboardMetrics()
        setEmployeeMetrics(metrics)
      } catch (error) {
        console.error('Error loading employee metrics:', error)
      } finally {
        setIsLoadingMetrics(false)
      }
    }

    // Only load for regular employees (not managers or admins)
    if (!isManager && !isAdmin) {
      loadEmployeeMetrics()
    } else {
      setIsLoadingMetrics(false)
    }
  }, [isManager, isAdmin, user?.role])

  const copyDebugInfo = () => {
    if (debugInfo) {
      navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Show access request message for users with 'padrao' role
  if (user!.role === 'padrao') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="card-hover max-w-2xl w-full text-center p-8 sm:p-12">
          <div className="w-20 h-20 bg-gradient-to-br from-brand-primary/20 to-brand-accent-600/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-monumenta">
            <AlertTriangle className="w-10 h-10 text-brand-primary" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-4 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Acesso Pendente
          </h2>
          <p className="font-body text-brand-gray-light text-lg mb-6">
            Olá, <span className="text-brand-primary font-medium">{user?.name}</span>!
          </p>
          <p className="font-body text-brand-gray mb-8 leading-relaxed">
            Sua conta foi criada com sucesso, mas você ainda não possui permissões de acesso ao sistema.
            <br />
            Por favor, entre em contato com o administrador para solicitar as permissões necessárias.
          </p>
          <div className="bg-brand-dark-lighter border border-brand-primary/20 rounded-lg p-6 mb-6">
            <p className="font-body text-sm text-brand-gray-light">
              <span className="text-brand-primary font-semibold">Próximos passos:</span>
              <br />
              Entre em contato com o administrador do sistema para solicitar acesso às funcionalidades da plataforma.
            </p>
          </div>
          <div className="text-sm text-brand-gray mb-6">
            Seu email: <span className="text-white font-medium">{user?.email}</span>
          </div>

          {/* Clock Skew Warning Banner */}
          {debugInfo?.clockSkewDetected && (
            <div className="bg-red-900/30 border-2 border-red-500 rounded-lg p-4 mb-6 animate-pulse">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-red-300 mb-2">
                    Relógio do Sistema Dessinconizado
                  </h3>
                  <p className="text-sm text-red-200 mb-3">
                    O relógio do seu computador está <strong className="text-white">{debugInfo.clockSkewMinutes}</strong> dessinconizado com o servidor.
                    Isso impede o acesso ao sistema.
                  </p>
                  <div className="bg-red-950/50 border border-red-700 rounded p-3 mb-3">
                    <p className="text-xs font-semibold text-red-300 mb-2">Como corrigir:</p>
                    <ol className="text-xs text-red-200 space-y-1 list-decimal list-inside">
                      <li>Abra as configurações de Data e Hora do Windows</li>
                      <li>Ative "Definir hora automaticamente"</li>
                      <li>Clique em "Sincronizar agora"</li>
                      <li>Feche todas as abas e faça login novamente</li>
                    </ol>
                  </div>
                  <p className="text-xs text-red-300 italic">
                    📌 Este é um problema de segurança do AWS que requer sincronização precisa de tempo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Debug Information Toggle */}
          <div className="border-t border-brand-primary/20 pt-6">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="mx-auto text-sm text-brand-gray hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              <ChevronDown
                className={`w-4 h-4 transition-transform ${showDebug ? 'rotate-180' : ''}`}
              />
              {showDebug ? 'Ocultar Informações Técnicas' : 'Mostrar Informações Técnicas'}
            </button>

            {showDebug && debugInfo && (
              <div className="mt-4 bg-brand-dark-lighter rounded-lg p-4 border border-brand-primary/20 text-left">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-brand-gray-light uppercase tracking-wide">
                    Diagnóstico para Suporte
                  </h3>
                  <button
                    onClick={copyDebugInfo}
                    className="text-xs px-2 py-1 bg-brand-primary/20 hover:bg-brand-primary/30 text-white rounded transition-colors"
                  >
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>

                <div className="space-y-2 text-xs font-mono">
                  <div className="flex flex-col">
                    <span className="text-brand-gray">Timestamp:</span>
                    <span className="text-white break-all">{debugInfo.timestamp}</span>
                  </div>

                  {debugInfo.error ? (
                    <div className="flex flex-col">
                      <span className="text-red-400">Error:</span>
                      <span className="text-red-300 break-all">{debugInfo.error}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col">
                        <span className="text-brand-gray">Cognito Sub:</span>
                        <span className="text-white break-all">{debugInfo.cognitoSub}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">User ID:</span>
                        <span className="text-white break-all">
                          {debugInfo.userProfile?.userId || debugInfo.userProfile?.id || 'N/A'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">Status:</span>
                        <span className="text-white break-all">
                          {debugInfo.userProfile?.status || 'N/A'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">Role:</span>
                        <span className="text-white break-all">
                          {debugInfo.userProfile?.role || debugInfo.userProfile?.roleName || 'N/A'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">Token Válido:</span>
                        <span className={debugInfo.tokenValid ? 'text-green-400' : 'text-red-400'}>
                          {debugInfo.tokenValid ? 'Sim' : 'Não'} ({debugInfo.timeUntilExpiry})
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">Identity ID:</span>
                        <span className="text-white break-all">{debugInfo.identityId}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">Session Tokens:</span>
                        <span className={debugInfo.sessionTokensPresent ? 'text-green-400' : 'text-red-400'}>
                          {debugInfo.sessionTokensPresent ? 'Presentes' : 'Ausentes'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="text-brand-gray">Clock Skew:</span>
                        <span className={debugInfo.clockSkewDetected ? 'text-red-400 font-bold' : 'text-green-400'}>
                          {debugInfo.clockSkewDetected ? `DETECTADO: ${debugInfo.clockSkewMinutes}` : 'OK'}
                        </span>
                      </div>

                      {debugInfo.apiError && (
                        <div className="flex flex-col">
                          <span className="text-brand-gray">API Error:</span>
                          <span className="text-red-400 break-all text-xs">{debugInfo.apiError}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-brand-primary/20">
                  <p className="text-xs text-brand-gray">
                    <strong className="text-white">Compartilhe essas informações</strong> com o suporte técnico para diagnóstico mais rápido.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Show enhanced home screen for managers
  if (user!.role === 'diretor-tecnico') {
    return (
      <TechDirectorDashboardNew />
    )
  }
  // isManager (ceo, vp, dir-executivo) have employee records — EnhancedHomeScreen works for them
  // isAdmin does NOT have an employee record — skip EnhancedHomeScreen and fall through to metrics view
  if (isManager) {
    return (
      <EnhancedHomeScreen />
    )
  }

  return (
      <div className="space-y-6 sm:space-y-8">
        {/* Header with Welcome Message */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-end">
          <div>
            <div className="flex items-center space-x-2 text-sm text-brand-gray">
              <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
              <span>Última atualização: agora</span>
            </div>
          </div>
        </div>

        {/* Metrics Cards - Admin and Manager with Real Data */}
        {(isAdmin || isManager) && (
          <div className="dashboard-grid lg:grid-cols-6">
            <MetricCard
              title="Total de Colaboradores"
              value={COMPANY_METRICS.totalWorkforce}
              change="+5 este mês"
              icon={Users}
              trend="up"
            />
            <MetricCard
              title="Investimento Anual"
              value={realFormatCurrency(COMPANY_METRICS.totalAnnualInvestment)}
              change="Total workforce"
              icon={DollarSign}
              trend="neutral"
            />
            <MetricCard
              title="Burn Rate Mensal"
              value={realFormatCurrency(COMPANY_METRICS.monthlyBurnRate)}
              change="Média mensal"
              icon={TrendingUp}
              trend="neutral"
            />
            <MetricCard
              title="Custo por Colaborador"
              value={realFormatCurrency(COMPANY_METRICS.averageCostPerEmployee)}
              change="Média anual"
              icon={Calculator}
              trend="neutral"
            />
            {/* <MetricCard
              title="Alertas Críticos"
              value={vacationAlerts.filter(alert => alert.severity === 'critical').length}
              change="Férias acumuladas"
              icon={AlertTriangle}
              trend="down"
            />
            <MetricCard
              title="Avisos de Férias"
              value={vacationAlerts.filter(alert => alert.severity === 'warning').length}
              change="Programar breve"
              icon={Clock}
              trend="neutral"
            /> */}
          </div>
        )}

        {/* Employee-specific metrics */}
        {user!.role === 'employee' && (
          <div className="dashboard-grid lg:grid-cols-4">
            {/* Vacation Days */}
            {isLoadingMetrics ? (
              <MetricCard
                title="Dias de Férias Disponíveis"
                value="..."
                icon={Calendar}
              />
            ) : employeeMetrics?.vacation ? (
              <MetricCard
                title="Dias de Férias Disponíveis"
                value={employeeMetrics.vacation.available}
                change={
                  employeeMetrics.vacation.expiringCount
                    ? `${employeeMetrics.vacation.expiringCount} expirando`
                    : `${employeeMetrics.vacation.used} usados`
                }
                icon={Calendar}
                trend={employeeMetrics.vacation.expiringCount ? 'down' : 'neutral'}
              />
            ) : (
              <EmptyMetricCard
                title="Dias de Férias"
                icon={Calendar}
                message="Dados não disponíveis"
              />
            )}

            {/* Training */}
            {isLoadingMetrics ? (
              <MetricCard
                title="Treinamentos Concluídos"
                value="..."
                icon={Award}
              />
            ) : employeeMetrics?.training ? (
              <MetricCard
                title="Treinamentos Concluídos"
                value={employeeMetrics.training.completed}
                change={
                  employeeMetrics.training.inProgress > 0
                    ? `${employeeMetrics.training.inProgress} em andamento`
                    : undefined
                }
                icon={Award}
                trend={employeeMetrics.training.inProgress > 0 ? 'up' : 'neutral'}
              />
            ) : (
              <EmptyMetricCard
                title="Treinamentos"
                icon={Award}
                message="Dados não disponíveis"
              />
            )}

            {/* Banco de Horas */}
            {isLoadingMetrics ? (
              <MetricCard
                title="Banco de Horas"
                value="..."
                icon={Clock}
              />
            ) : employeeMetrics?.bancoDeHoras ? (
              <MetricCard
                title="Banco de Horas"
                value={employeeMetrics.bancoDeHoras.balance}
                icon={Clock}
                trend={employeeMetrics.bancoDeHoras.balanceMinutes > 0 ? 'up' : employeeMetrics.bancoDeHoras.balanceMinutes < 0 ? 'down' : 'neutral'}
              />
            ) : (
              <EmptyMetricCard
                title="Banco de Horas"
                icon={Clock}
                message="Dados não disponíveis"
              />
            )}

            {/* Next Evaluation */}
            {isLoadingMetrics ? (
              <MetricCard
                title="Próxima Avaliação"
                value="..."
                icon={CheckCircle}
              />
            ) : employeeMetrics?.nextEvaluation ? (
              <MetricCard
                title="Próxima Avaliação"
                value={new Date(employeeMetrics.nextEvaluation.date).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}
                icon={CheckCircle}
              />
            ) : (
              <EmptyMetricCard
                title="Próxima Avaliação"
                icon={CheckCircle}
                message="Não agendada"
              />
            )}
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Chart for Admin/Manager or Personal Stats for Employee */}
            {(isAdmin || isManager) ? (
              <div className="card-hover">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-white">
                    Onboardings em Andamento
                  </h3>
                </div>
                <EmptyState
                  icon={Users}
                  title="Nenhum Onboarding Ativo"
                  description="Todos os processos de integração foram concluídos com sucesso"
                  variant="success"
                  compact
                />
              </div>
            ) : (
              <div className="card-hover">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-white">
                    Meu Desenvolvimento
                  </h3>
                  {employeeMetrics?.development && employeeMetrics.development.skills.length > 0 && (
                    <span className="status-primary">Em progresso</span>
                  )}
                </div>
                {isLoadingMetrics ? (
                  <div className="space-y-6">
                    <div className="group">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-white text-sm sm:text-base font-display">Carregando...</span>
                        <span className="text-brand-primary font-display font-semibold">...</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill animate-pulse" style={{ width: '50%' }}></div>
                      </div>
                    </div>
                  </div>
                ) : employeeMetrics?.development && employeeMetrics.development.skills.length > 0 ? (
                  <div className="space-y-6">
                    {employeeMetrics.development.skills.slice(0, 3).map((skill, index) => (
                      <div key={index} className="group">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-white text-sm sm:text-base font-display">{skill.name}</span>
                          <span className="text-brand-primary font-display font-semibold">{skill.progress}%</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill group-hover:animate-pulse" style={{ width: `${skill.progress}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={BookOpen}
                    title="Nenhum Plano de Desenvolvimento"
                    description="Entre em contato com seu gestor para definir seus objetivos de desenvolvimento"
                    compact
                  />
                )}
              </div>
            )}

            <RecentActivity />
          </div>

          {/* Right Column */}
          <div className="space-y-4 sm:space-y-6">
            <QuickActions userRole={user!.role} />
            <UpcomingEvents />
            {(isAdmin || isManager) && <DepartmentChart />}
          </div>
        </div>
      </div>
  )
}

export default function DashboardPage() {
  return (
    <ProtectedRoute>
      <DashboardContent />
    </ProtectedRoute>
  )
} 